import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { Drawer } from '@mui/material'
import { useAppStore } from '@/store/appStore'
import { SidebarContent } from './SidebarContent'

/**
 * Temporary drawer host for the sidebar below `lg`.
 *
 * The paper carries the `sidebar` class so the dark surface, tree styling and the
 * flex-column/overflow chain that ContentTree's scroll area depends on all apply
 * unchanged. MUI supplies focus trapping, Escape handling and the backdrop.
 */
export function MobileNavDrawer() {
  const { t } = useTranslation()
  const location = useLocation()
  const mobileNavOpen = useAppStore((s) => s.mobileNavOpen)
  const setMobileNavOpen = useAppStore((s) => s.setMobileNavOpen)
  const overlayPage = useAppStore((s) => s.overlayPage)

  // Any navigation dismisses the drawer: tree selections, breadcrumbs and the
  // supplementary nav links all route through here.
  useEffect(() => {
    setMobileNavOpen(false)
  }, [location.pathname, setMobileNavOpen])

  // Overlay pages (Glossary, FAQ, ...) are opened from inside the drawer but are
  // not routes, so they need their own dismissal.
  useEffect(() => {
    if (overlayPage) setMobileNavOpen(false)
  }, [overlayPage, setMobileNavOpen])

  return (
    <Drawer
      variant="temporary"
      anchor="left"
      open={mobileNavOpen}
      onClose={() => setMobileNavOpen(false)}
      ModalProps={{ keepMounted: true }}
      slotProps={{
        paper: {
          id: 'app-sidebar-drawer',
          className: 'sidebar sidebar--drawer',
          'aria-label': t('sidebar.navigationSidebar'),
          // The surface colours MUST come from sx, not the `sidebar` class: emotion
          // injects .MuiPaper-root's white background after responsive.css at equal
          // specificity, so a plain class rule always loses. sx outranks it.
          sx: {
            backgroundColor: 'var(--color-sidebar-bg)',
            backgroundImage: 'none',
            color: 'var(--color-sidebar-text)',
          },
        },
      }}
    >
      <SidebarContent variant="drawer" />
    </Drawer>
  )
}
